import { Bell, PlusSquare, UserCircle2, Wallet } from 'lucide-react';
import { Session } from 'next-auth';
import { ReactElement } from 'react';

import { OrgsPublicTableInterface } from '@/types/database-types';
import { getAccountUsersUrl, getUpgradeAccountUrl } from '@/utils/helpers/urls';
import { getUserIsAdmin, getUserIsOnFreeTier } from '@/utils/permissions/helpers';

interface Action {
  href: string;
  label: string;
  icon: ReactElement;
}

export const getAvailableNavActions = (session: Session): Record<'workspaceActions' | 'personalActions', Action[]> => {
  const { user } = session;
  if (!user)
    return {
      workspaceActions: [],
      personalActions: [],
    };

  const isChildSubscription = !!session?.subscription.isChild;
  const userIsOnFreeTier = getUserIsOnFreeTier(session?.subscription);
  const userIsAdmin = getUserIsAdmin(user);
  const shouldShowInviteMembers = !userIsOnFreeTier && userIsAdmin;
  const shouldShowPlanAndUsage = !userIsOnFreeTier && !isChildSubscription;

  return {
    workspaceActions: [
      shouldShowPlanAndUsage && { href: getUpgradeAccountUrl(), label: 'Plan & usage', icon: <Wallet size={16} /> },
      shouldShowInviteMembers && {
        href: getAccountUsersUrl(),
        label: 'Invite members',
        icon: <PlusSquare size={16} />,
      },
    ].filter((action): action is Action => Boolean(action)),
    personalActions: [
      { href: '/account', label: 'Manage account', icon: <UserCircle2 size={16} /> },
      {
        href: '/account/notifications',
        label: 'Manage notifications',
        icon: <Bell size={16} />,
      },
    ],
  };
};

export const sortOrgsByParentChild = (orgs: OrgsPublicTableInterface[] | null): OrgsPublicTableInterface[] | null => {
  if (!orgs?.length) return orgs;

  const idSet = new Set(orgs.map((org) => org.id).filter(Boolean) as string[]);
  const childrenByParent = new Map<string, OrgsPublicTableInterface[]>();
  const roots: OrgsPublicTableInterface[] = [];
  const orphans: OrgsPublicTableInterface[] = [];

  orgs.forEach((org) => {
    const parentId = org.parentOrgId;
    if (!parentId) {
      roots.push(org);
    } else if (!idSet.has(parentId)) {
      orphans.push(org);
    } else {
      const list = childrenByParent.get(parentId) ?? [];
      list.push(org);
      childrenByParent.set(parentId, list);
    }
  });

  const result: OrgsPublicTableInterface[] = [];

  const addWithChildren = (org: OrgsPublicTableInterface) => {
    result.push(org);
    const children = childrenByParent.get(org.id || '');
    children?.forEach(addWithChildren);
  };

  roots.forEach(addWithChildren);
  result.push(...orphans);

  return result;
};
